import React from 'react';
import { connect, } from 'react-redux';
import { useParams } from 'react-router-dom';
import { handleAuctionByIdFetch } from '../../Auctions/store/Auctions';
import { handleLotsDataFetch } from '../../Lots/store/Lots';
import { HiOutlineLocationMarker, HiOutlinePhone, HiOutlineMail } from "react-icons/hi";
import { MdDateRange, MdAccessTime, } from "react-icons/md";
import { ImListNumbered } from "react-icons/im";
import { AuctionTypeEnum } from '@/lib/utils/AuctionTypeEnum';
import useStyles from "./CatalogPrintStyles";
import { formatDateTime } from '../../../lib/utils/General';
import { CurrencyLabel } from '../../../components/composite/CurrencyLabel';

interface Props {
    handleAuctionByIdFetch_d: (auctionId: string) => void,
    handleLotsDataFetch_d: (auctionId: string, onCompletedCallback?: (res: any) => void) => void,
    componentLoaded: () => void,
    auctionData: any,
    lotsData: any,
    settings: any
}

const ComponentToPrint: React.FC<Props> = props => {

    const { classes } = useStyles();

    const {
        handleAuctionByIdFetch_d,
        handleLotsDataFetch_d,
        componentLoaded,
        auctionData,
        lotsData,
        settings
    } = props;

    const { auctionId }: any = useParams();

    const [lotFeatures, setLotFeatures]: any = React.useState([]);
    const [currentAuctiontype, setCurrentAuctionType]: any = React.useState(0);

    React.useEffect(() => {
        if (auctionId) {
            handleAuctionByIdFetch_d(auctionId)
        }
    }, [auctionId])

    React.useEffect(() => {
        if (auctionData) {
            setCurrentAuctionType(auctionData.auctionType);
            handleLotsDataFetch_d(auctionId);
        }
    }, [auctionData])

    React.useEffect(() => {
        if (lotsData) {
            var allFeaturesArr: any = [];

            for (var i = 0; i < lotsData.length; i++) {
                var featuresArr: any = [];

                lotsData[i]?.dataCaptured?.additionalProperties?.map(x => {
                    if (x.name !== '' && x.value !== '' && !x.hidden)
                        featuresArr.push({ ...x, key: x.name });
                });

                allFeaturesArr.push(featuresArr);
            }

            setLotFeatures(allFeaturesArr)
            componentLoaded();
        }

    }, [lotsData])

    const getSTCStatus = (paramStcStatus) => {
        if (paramStcStatus.toString() === '0') {
            return 'STC No';
        } else if (paramStcStatus.toString() === '1') {
            return 'STC Yes';
        } else if (paramStcStatus.toString() === '2') {
            return 'STC Awaiting';
        }
    }

    // Print-friendly modern layout: flat colours, hairline borders, no shadows, blur or transparency,
    // so the browser keeps the PDF as vector text instead of rasterising effects.
    return (
        <div className="pb-cat">
            {auctionData && lotsData && (
                <header className="pb-cat-head">
                    <div className="pb-cat-brand">
                        <img src={settings.VendorLogoUrl} className={classes.logo} alt={settings.VendorName} />
                        <div className="pb-cat-contact">
                            {settings.VendorContactNumber && <div><HiOutlinePhone /> {settings.VendorContactNumber}</div>}
                            {settings.VendorEmailAddress && <div><HiOutlineMail /> {settings.VendorEmailAddress}</div>}
                            {settings.VendorAddress && <div><HiOutlineLocationMarker /> {settings.VendorAddress}</div>}
                        </div>
                    </div>
                    <h1 className="pb-cat-title">{auctionData.name}</h1>
                    <div className="pb-cat-stats">
                        <div className="pb-cat-stat"><MdDateRange /><div><span>{settings.AuctionName + " Date"}</span><strong>{formatDateTime(auctionData.startDateTimeAt, "yyyy/MM/dd")}</strong></div></div>
                        <div className="pb-cat-stat"><MdAccessTime /><div><span>{settings.AuctionName + " Time"}</span><strong>{formatDateTime(auctionData.startDateTimeAt, "HH:mm")}</strong></div></div>
                        <div className="pb-cat-stat"><ImListNumbered /><div><span>{"No. of " + settings.LotName + "s"}</span><strong>{lotsData.length}</strong></div></div>
                    </div>
                </header>
            )}

            {auctionData && lotsData && lotsData.map((lot, idx) => (
                <section className="pb-cat-lot" key={lot.id ?? idx}>
                    <div className="pb-cat-image">
                        <img src={lot.imageUrl} alt="" />
                    </div>
                    <div className="pb-cat-body">
                        <div className="pb-cat-chips">
                            {!lot.isActive && <span className="pb-cat-chip pb-cat-chip--dark">Unavailable</span>}
                            <span className="pb-cat-chip">{getSTCStatus(lot.stcStatus)}</span>
                            <span className="pb-cat-chip pb-cat-chip--brand">{"Buyer's Commission " + lot.commission + "%"}</span>
                            {Number(currentAuctiontype) !== Number(AuctionTypeEnum.Tender.toString()) && (
                                <span className="pb-cat-chip pb-cat-chip--info">
                                    <CurrencyLabel textColour="#0B7F99" value={lot.startingPrice} prefixString="Opening Bid " />
                                </span>
                            )}
                        </div>
                        <h2 className="pb-cat-name"><span>{lot.number} | </span>{lot.name}</h2>
                        {lotFeatures[idx] && lotFeatures[idx].length > 0 && (
                            <div className="pb-cat-features">
                                {lotFeatures[idx].filter((x, index) => index <= 5).map((lotFeature, i) => (
                                    <div key={"lotF-" + i}>
                                        <span>{lotFeature.key}</span>
                                        <strong>{lotFeature.value}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            ))}
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    auctionData: state.auctions.auction,
    lotsData: state.lots.lotsData,
    error: state.lots.error,
    errorText: state.lots.errorText,
    success: state.lots.success,
    settings: state.settings.settings,
})

const mapDispatchToProps = (dispatch: any) => {
    return {
        handleAuctionByIdFetch_d: (auctionId: string) => dispatch(handleAuctionByIdFetch(auctionId)),
        handleLotsDataFetch_d: (auctionId: string, onCompletedCallback?: (res: any) => void) => dispatch(handleLotsDataFetch(auctionId, onCompletedCallback)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ComponentToPrint);